import {
  Building2,
  LayoutGrid,
  LayoutPanelLeft,
  LayoutPanelTop,
  Settings,
  User,
} from "lucide-react";
import { useCanAccess, useGetIdentity, useUserMenu } from "ra-core";
import { Link } from "react-router";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { usePlatformOperator } from "@/modules/platform/platformApi";
import { ProfilePage } from "../settings/ProfilePage";
import { useNavigationLayoutPreference } from "./navigationLayoutPreference";

export const CRMUserMenuItems = () => (
  <>
    <SettingsMenu />
    <ProfileMenu />
    <NavigationLayoutMenu />
    <WorkspaceAdminMenu />
  </>
);

/**
 * Apps (workspace admins turn modules on and off) and Platform (operators
 * only) — moved here from the bottom of the sidebar.
 */
const WorkspaceAdminMenu = () => {
  const userMenuContext = useUserMenu();
  const { data: identity } = useGetIdentity();
  const { data: isOperator } = usePlatformOperator();
  const isWorkspaceAdmin =
    (identity as { administrator?: boolean } | undefined)?.administrator ===
    true;
  // Viewing as a company: show its menu, not the platform console.
  const isPlatformOperator =
    isOperator === true &&
    (identity as { viewing_as?: unknown } | undefined)?.viewing_as == null;

  if (!userMenuContext || (!isWorkspaceAdmin && !isPlatformOperator)) {
    return null;
  }

  return (
    <>
      <DropdownMenuSeparator />
      {isWorkspaceAdmin ? (
        <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
          <Link to="/apps" className="flex items-center gap-2">
            <LayoutGrid />
            Apps
          </Link>
        </DropdownMenuItem>
      ) : null}
      {isPlatformOperator ? (
        <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
          <Link to="/platform" className="flex items-center gap-2">
            <Building2 />
            Platform
          </Link>
        </DropdownMenuItem>
      ) : null}
    </>
  );
};

const SettingsMenu = () => {
  const userMenuContext = useUserMenu();
  const { canAccess: canEditSettings, isPending: settingsPending } =
    useCanAccess({ resource: "configuration", action: "edit" });
  const { canAccess: canManageUsers, isPending: usersPending } = useCanAccess({
    resource: "organization_members",
    action: "edit",
  });

  if (!userMenuContext) {
    throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  }

  if (settingsPending || usersPending) {
    return null;
  }

  if (!canEditSettings && !canManageUsers) {
    return null;
  }

  const settingsPath = canEditSettings ? "/settings" : "/settings?tab=users";

  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={settingsPath} className="flex items-center gap-2">
        <Settings />
        Settings
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ProfilePage.path} className="flex items-center gap-2">
        <User />
        Profile
      </Link>
    </DropdownMenuItem>
  );
};

const NavigationLayoutMenu = () => {
  const userMenuContext = useUserMenu();
  const [mode, setMode] = useNavigationLayoutPreference();

  if (!userMenuContext) {
    throw new Error("<NavigationLayoutMenu> must be used inside <UserMenu>");
  }

  const isSidebar = mode === "sidebar";

  return (
    <DropdownMenuItem
      onClick={() => {
        setMode(isSidebar ? "top" : "sidebar");
        userMenuContext.onClose();
      }}
      className="gap-2"
    >
      {isSidebar ? (
        <>
          <LayoutPanelTop />
          Top navigation
        </>
      ) : (
        <>
          <LayoutPanelLeft />
          Sidebar navigation
        </>
      )}
    </DropdownMenuItem>
  );
};
